// Assign catalog benefits to a client (customer or company). Stores rows in
// `client_benefits` (entity_type+entity_id ↔ benefit_id). Reused on both edit
// pages: <ClientBenefits entityType="customer" entityId={id} />.
import React, { useEffect, useState, useCallback } from "react";
import { Card, Text, BlockStack, Checkbox, Spinner } from "@shopify/polaris";
import supabase from "../../../supabase-client";
import { UserAuth } from "../../../context/AuthContext";

function ClientBenefits({ entityType, entityId, title = "Beneficios" }) {
  const { session } = UserAuth();
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [cat, assigned] = await Promise.all([
      supabase.from("benefits").select("*").eq("active", true).order("id"),
      entityId
        ? supabase
            .from("client_benefits")
            .select("benefit_id")
            .eq("entity_type", entityType)
            .eq("entity_id", entityId)
        : Promise.resolve({ data: [] }),
    ]);
    setCatalog(cat.data || []);
    setSelected(new Set((assigned.data || []).map((r) => r.benefit_id)));
    setLoading(false);
  }, [entityType, entityId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (benefitId, checked) => {
    if (!entityId) return;
    setBusyId(benefitId);
    try {
      if (checked) {
        await supabase.from("client_benefits").upsert(
          {
            entity_type: entityType,
            entity_id: entityId,
            benefit_id: benefitId,
            created_by: session?.user?.id || null,
          },
          { onConflict: "entity_type,entity_id,benefit_id" }
        );
        setSelected((prev) => new Set(prev).add(benefitId));
      } else {
        await supabase
          .from("client_benefits")
          .delete()
          .eq("entity_type", entityType)
          .eq("entity_id", entityId)
          .eq("benefit_id", benefitId);
        setSelected((prev) => {
          const n = new Set(prev);
          n.delete(benefitId);
          return n;
        });
      }
    } catch (e) {
      alert("Error al actualizar beneficios: " + (e.message || e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card>
      <BlockStack gap="300">
        <Text variant="headingMd">{title}</Text>
        {!entityId ? (
          <Text tone="subdued">Guarda el cliente primero para asignar beneficios.</Text>
        ) : loading ? (
          <Spinner size="small" />
        ) : catalog.length === 0 ? (
          <Text tone="subdued">No hay beneficios configurados.</Text>
        ) : (
          <BlockStack gap="150">
            {catalog.map((b) => (
              <Checkbox
                key={b.id}
                label={b.name}
                checked={selected.has(b.id)}
                disabled={busyId === b.id}
                onChange={(v) => toggle(b.id, v)}
              />
            ))}
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );
}

export default ClientBenefits;
